import React, { useRef, useEffect, useCallback, useState } from 'react';
import { fetch, fetchByJSON, GetDataFrameInfo, pythonEscape, useCachedData, toUnicode } from '../../util/util'
import './index.css'
import { push } from 'connected-react-router'
import { useSelector, useDispatch } from 'react-redux'
import { actions as DataSetActions } from '../../reducer/dataset'
import { Modal } from '../../util/ui'
import { Button, MultiSelect, DropDown, ButtonGroup, Label } from '../../util/ui_components'
import { InlineTip } from '../common/tip';
import Table from '../common/table'
import authHeader from '../../services/auth-header';
import { data } from 'autoprefixer';
import Tip from '../common/tip'
import Sandbox from '../common/sandbox'
import sanitizeJsonString from '../common/sanitize';
import { thebeConfigR } from '../../util/thebeConfigs';
import { usePageKernel, attachCells } from '../../util/thebeSession';

// const reconnectErrorFunction = () => {
//   alert('Kernel is taking too much time. Please reduce the data size!')
// }

const reconnectErrorFunction = () => {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: transparent;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 48px;
    height: 48px;
    border: 5px solid rgba(63, 91, 203, 0.84);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  `;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
  `;

  document.head.appendChild(style);
  overlay.appendChild(spinner);
  document.body.appendChild(overlay);

  setTimeout(() => {
    overlay.remove();
    style.remove();
  }, 1000);
};


const getInitialCode = data => `
library(jsonlite)
data_json <- '${data}'
# Convert JSON to a list. pandas' df.to_json() omits the row-index key entirely for any
# NaN cell (rather than including it as null), so columns with missing values end up with
# fewer entries than columns with none -- reindex every column against the union of all
# row indices first, so missing cells become proper NA in the right position, before
# combining into a data frame.
df_list <- fromJSON(data_json)
all_indices <- unique(unlist(lapply(df_list, names)))
all_indices <- all_indices[order(as.numeric(all_indices))]
columns_list <- lapply(df_list, function(col) {
    col_names <- names(col)
    sapply(all_indices, function(idx) {
        if (idx %in% col_names) {
            val <- col[[idx]]
            if (is.null(val)) NA else val
        } else {
            NA
        }
    })
})
names(columns_list) <- names(df_list)

# Combine the list into a proper data frame
df <- as.data.frame(columns_list, stringsAsFactors = FALSE, check.names = FALSE)
df <- type.convert(df, as.is = TRUE)
`

const supportCode = `
library(readr) # For reading CSV files
library(dplyr) # For data manipulation
library(ggplot2) # For data visualization

# Manually load other libraries if necessary

# Replace '<filename.csv>' with the dataset you need
df <- read_csv('<filename.csv>')
`

const getCodeFromConditions = ({ conditions }) => {
    let cleaners = []
for(let val of conditions){
    let { option,condition } =  val
    if (option == 0){
        cleaners.push(`df <- na.omit(df)`)
    }

    if (option == 1){
        cleaners.push(`df <- df[, !sapply(df, function(x) any(is.na(x)))]`)
    }

    if (option == 2){
        let { cols,items } = condition
        for(let col of cols){
            cleaners.push(`mean_value <- mean(as.numeric(df[["${col}"]]), na.rm = TRUE)
df[["${col}"]][is.na(df[["${col}"]])] <- mean_value`)
        }
    }

    if (option == 3){
        let { cols,items } = condition
        for(let col of cols){
            cleaners.push(`median_value <- median(as.numeric(df[["${col}"]]), na.rm = TRUE)
df[["${col}"]][is.na(df[["${col}"]])] <- median_value`)
        }
    }

    if (option == 4){
        let { cols,items } = condition
        for(let item of items){
            cleaners.push(`
if(!is.na(suppressWarnings(as.numeric(${item.val})))) {
    replacement_value <- as.numeric(${item.val})
} else {
    replacement_value <- as.character(${item.val})
}
df[["${item.col}"]][is.na(df[["${item.col}"]])] <- replacement_value
            `
            )
        }
    }

    if (option == 5){
        let { cols } = condition
        if (cols && cols.length > 0) {
            cleaners.push(`df <- df[!duplicated(df[c(${cols.map(col => `"${col}"`).join(', ')})]), ]`)
        } else {
            cleaners.push(`df <- df[!duplicated(df), ]`)
        }
    }
}

return `
if (!requireNamespace("dplyr", quietly = TRUE)) {
    install.packages("dplyr")
}
library(dplyr)
library(jsonlite)

# track all types of missing value
MISSING_VALUES <- c('-', '?', 'na', 'n/a', 'NA', 'N/A', 'nan', 'NAN', 'NaN', '*', '¶', '¶¶', '**')

# store cleaning option(s)
cleaners <- fromJSON('${JSON.stringify(conditions)}')

# clean dataset based on selected option(s)
df <- df %>%
  mutate(across(everything(), ~replace(., . %in% MISSING_VALUES, NA)))
${cleaners.join('\n')}

# display dataset after cleaning (first 10 rows only)
print(head(df, 10))
`
}

const CleanTypes = ['Remove N/A Rows', 'Remove N/A Columns', 'Replace N/A By Mean', 'Replace N/A By Median', 'Replace N/A By Specific Value', 'Remove Duplicates'];

const CleanTypesStyles = ['', '', '', '', '', '', '']
//                        0                    1                       2                     3                          4                             5                   
const CleaningR = ({ location }) => {
    useCachedData()
    let multiSelect23Ref = useRef()
    let [option, setOption] = useState(-1)
    let [optionText, setOptionText] = useState('Select cleaning type')
    let dataset = useSelector(state => state.dataset)
    const getDefaultSubOptions = useCallback(() => {
        let res = [...Array(6).keys()].map(e => ({ condition: {} }))
        res[4].refs = {}
        return res
    }, [])
    const [code, setCode] = useState('')
    let codeParent = useRef()
    let kernelRef = useRef()
    // one kernel per mounted page, shut down again on unmount
    const getPageKernel = usePageKernel(thebeConfigR)
    let [dfJSON, setDfJSON] = useState('')//dataframe json
    const [activateStatus, setActivateStatus] = useState('Loading...')
    let cleaningCondition = useRef(getDefaultSubOptions())
    let [subOptionText, setSubOptionText] = useState('Input values')
    let [showSubOptionModal, setShowSubOptionModal] = useState(false)
    let dispatch = useDispatch()
    let sandboxRef = useRef(null)
    // this "currentCleaner" hook only store the latest ONE applied cleaner filter
    let [currentCleaner, setCurrentCleaner] = useState([])

    useEffect(() => {
        reconnectErrorFunction();
        if (!code) return
        codeParent.current.innerHTML = ''
        let pre = document.createElement('pre')
        pre.setAttribute('data-executable', 'true')
        pre.setAttribute('data-language', 'R')
        codeParent.current.appendChild(pre)
        pre.innerHTML = code
        let isActive = true

        // Only the freshly created <pre> needs rendering here -- bootstrap() would also
        // request another kernel, and thebe's "status" bus is global, so the handler this
        // used to register kept firing (for every page's kernel) after unmount.
        getPageKernel().then((kernel) => {
            if (!isActive) return
            kernelRef.current = kernel
            kernelRef.current._reconnect = reconnectErrorFunction;
            attachCells(kernel)
        }).catch((err) => console.error('Error connecting to the R kernel:', err))

        return () => { isActive = false }
    }, [code])

    useEffect(() => {
        reconnectErrorFunction();
        if (!dataset.filename) {
            setActivateStatus('No data')
            return
        }

        // The kernel connection is (re-)established by the [code] effect above whenever
        // the visible cell is (re)created -- this effect used to also call
        // thebelab.bootstrap() here, which could request a second, independent kernel,
        // causing df to sometimes get built (by runCode()'s boilerplate step) on a
        // different kernel than the one the visible cell's own run button actually
        // executes against. It only needs to (re-)fetch the current dataset's JSON.
        let isActive = true
        async function loadData() {
            let res = await fetchByJSON('current_data_json', {
                filename: dataset.filename
            })

            let g = await res.json()
            if (!isActive) return
            let json_string = sanitizeJsonString(g.data);
            setDfJSON(json_string)
            setActivateStatus('Ready')
        }
        loadData()

        return () => { isActive = false }
    }, [dataset.filename])

    const runCode = async (e) => {
        try {
            setActivateStatus('Running Initial Code ...');
        let res = await fetchByJSON('current_data_json', {
            filename: dataset.filename
        })

        let json = await res.json();
        let json_string = sanitizeJsonString(json.data);
        // let res2 = await kernelRef.current.requestExecute({ code:getInitialCode(option,dfJSON.current) }).done
        let res2 = await kernelRef.current.requestExecute({ code:getInitialCode(json_string) }).done
        
        document.querySelector('.thebelab-run-button').click()
        setActivateStatus('Ready');
        } catch (err) {
            console.error("Error during run:", err);
            setActivateStatus('Error during run');
        }
    }

    const onConfirm = (e) => {
        if (option === -1) return

        dispatch(DataSetActions.setCleaners([...dataset.dataCleaners, ...currentCleaner]))
    }

    const onUndo = async (e) => {
        let res = await fetchByJSON('cleanEditedCache', {
            filename: dataset.filename
        })
        let json = await res.json()
        if (json.success) {
            let previous = dataset.dataCleaners.slice(0, -1)
            dispatch(DataSetActions.setCleaners(previous))
            setCurrentCleaner([])
            setCode(getCodeFromConditions({ conditions: previous }))
        }
    }

    const onRevert = async (e) => {
        if (dataset.filename) {
            let res = await fetchByJSON('cleanEditedCache', {
                filename: dataset.filename
            })

            let json = await res.json()

            if (json.success) {
                alert('Revert data success!')
                dispatch(DataSetActions.emptyInfo())

                // selectFileOption(dataset.filename, false)
                // replace the above function with the first part of selectFileOption() in /home/index.jsx
                let res2 = await fetch('/file/?filename=' + dataset.filename + '&default=' + (dataset.isDefault === true), {
                    method: 'GET',
                    headers: authHeader()
                })
                let json2 = await res2.json()
              
                if (json2.success) {
                    dispatch(DataSetActions.setData({
                        filename: dataset.filename,
                        info: GetDataFrameInfo(json2.info),
                        data: JSON.parse(json2.data),
                        cols: json2.cols,
                        num_cols: json2.num_cols,
                        col_lists: json2.col_lists,
                        cate_cols: json2.cate_cols,
                        cate_lists: json2.cate_lists,
                        num_lists: json2.num_lists
                    }))
                }
            }
        }
    }

    const onDownload = () => {
        const element = document.createElement('a')
        // const file = new Blob([getInitialCode(dfJSON.current), code], {type: "text/plain"})
        const file = new Blob([supportCode, code], {type: "text/plain"})
        element.href = URL.createObjectURL(file)
        element.download = "download_test.r" // or .txt
        document.body.appendChild(element)
        element.click()
    }

    const [guideStep, setGuideStep] = useState(0)
    useEffect(() => {
        switch (guideStep) {
            case 0:
                if (location.state?.guide) {
                    document.querySelector('#dropdownClean').classList.add('btn-blink')
                    setGuideStep(1)
                }
                break
            case 1:
                break
            case 2:
                document.querySelector('#dropdownClean').classList.remove('btn-blink')
                document.querySelector('#confirmBtn').classList.add('btn-blink')
                break
            case 3:
                document.querySelector('#confirmBtn').classList.remove('btn-blink')
                break
        }
    }, [guideStep])

    const onConfirmSubOption = () => {
        if (option === 4) {
            let items = []
            let refs = cleaningCondition.current[4].refs
            for (let p in refs) {
                let value = refs[p].value

                if (value)
                    items.push({
                        col: p,
                        val: refs[p].value
                    })
            }
            cleaningCondition.current[4].condition.items = items
            if (items.length) setSubOptionText('Edit values')
            setCurrentCleaner([{
                option: option,
                condition: {items: items},
                desc: CleanTypes[option]
            }])
        }


        setShowSubOptionModal(false)
    }

    const queryCleaner = async () => {
        
        let res = await fetchByJSON('clean', {
            cleaners: JSON.stringify(dataset.dataCleaners),
            filename: dataset.filename
        })

        let json = await res.json()
        dispatch(DataSetActions.setData({
            data: JSON.parse(json.data),
            cols: json.cols,
            num_cols: json.num_cols,
            col_lists: json.col_lists,
            cate_cols: json.cate_cols,
            cate_lists: json.cate_lists,
            num_lists: json.num_lists
        }))
        console.log(currentCleaner)
    }

    useEffect(() => {
        queryCleaner()
        setCode(getCodeFromConditions({ conditions: dataset.dataCleaners }))
    }, [dataset.dataCleaners])

    return (<div className='flex flex-col min-h-screen bg-gray-100'>
        <Modal isOpen={showSubOptionModal} setIsOpen={setShowSubOptionModal} onClose={onConfirmSubOption} contentStyleText="mx-auto mt-20" style={{ maxWidth: '35%' }}>
            <div className='p-5 flex flex-col'>
                <div className="flex flex-col">
                    <div className={`${option === 4 ? '' : 'hidden'}`}>
                        {dataset?.cols?.map(name => <div key={name} className="flex flex-row justify-between w-full">
                            <div className='py-3 px-10 label-left'>{name + ':'}</div>
                            <div className='py-3 label-right'>
                                <input ref={ref => cleaningCondition.current[4].refs[name] = ref} className='py-2 px-5 focus:outline-none rounded-full' placeholder="Specified Value" />
                            </div>
                        </div>)}
                    </div>
                </div>
                <div className="flex justify-end m-3 mt-10">
                    <Button text='Confirm' customStyleText='bordered-light' onClick={onConfirmSubOption} />
                </div>
            </div>
        </Modal>

        <div className="flex flex-row h-20 w-full items-center justify-start bg-gray-100 shadow-md">

            <div className='mx-5 w-3/12'>
                <DropDown id="dropdownClean" text={optionText} width='w-72' zIndex={100} items={
                    CleanTypes.map((item, i) => ({
                        name: item, onClick(e) {
                            {/*0                      1                2                       3                        4                            5 */ }
                            setOption(i)
                            setOptionText(item)
                            if (multiSelect23Ref.current) {
                                multiSelect23Ref.current.clear()
                            }
                            if (i === 4) {
                                setShowSubOptionModal(true)
                            }
                            if (guideStep == 1) {
                                setGuideStep(2)
                            }

                            if (guideStep == 2) {
                                setGuideStep(3)
                            }
                            if (i === 0 || i === 1) {
                                setCurrentCleaner([{option:i, condition: cleaningCondition.current[i].condition, desc: CleanTypes[i]}])
                            }
                            if (i === 5) {
                                // Remove duplicates - default is to remove all exact duplicates
                                setCurrentCleaner([{option:i, condition: {cols: []}, desc: CleanTypes[i]}])
                            }
                        }
                    }))} />
            </div>
            <div className='mx-5 w-3/12'>
                {/* Select a column and apply a cleaner */}
                {(option === 2 || option === 3) ? <MultiSelect ref={multiSelect23Ref} customHeight={`h-10`} selections={dataset.num_cols} /*defaultValue={dataset.num_cols} defaultText={dataset.num_cols[0]}*/
                    onSelect={(e) => {
                        // will cause error when re-select on this multiselect!!!
                        // cleaningCondition.current[option].condition.cols = e
                        // setCurrentCleaner([{option:option, condition: cleaningCondition.current[option].condition, desc: CleanTypes[option]}])
                        setCurrentCleaner([{
                            option: option,
                            condition: {cols: e},
                            desc: CleanTypes[option],
                        }])
                    }}
                /> : ''}

                {/* Remove Duplicates - Select columns for comparison */}
                {(option === 5) ? 
                    <div className="flex flex-col w-full">
                        <MultiSelect 
                            customHeight={`h-10`} 
                            selections={dataset.cols} 
                            defaultText="Select columns (optional)"
                            onSelect={(e) => {
                                setCurrentCleaner([{
                                    option: option,
                                    condition: {cols: e},
                                    desc: CleanTypes[option],
                                }])
                            }}
                        />
                        <div className="text-xs text-gray-500 mt-1 px-2">
                            {currentCleaner[0]?.condition?.cols?.length > 0 
                                ? `Remove rows with same values in: ${currentCleaner[0].condition.cols.join(', ')}`
                                : "Remove rows that are exactly the same across all columns"
                            }
                        </div>
                    </div>
                : ''}

                {/* Replace N/A By Specific Value, open a modal which contains col names and inputs */}
                {(option === 4) ?
                    <>
                        <Button onClick={() => setShowSubOptionModal(s => !s)} text={subOptionText} width='w-64' />
                    </>
                    : ''}
            </div>

            <div className='mx-5 w-3/12'>
                <MultiSelect defaultText={`Applied cleaners`} allowDelete={false} passiveMode={true} selections={dataset.dataCleaners} getDesc={e => e.desc} />
            </div>

            <div className='w-auto flex justify-center items-center px-1'>
                <div className={``}>{activateStatus}</div>
                <InlineTip zIndex={10} info='The loading status of a remote environment, R code will be executed in that environment as soon as it is ready.' />
            </div>

            
        </div>
        <div className="flex flex-row h-20 w-full items-center justify-start bg-gray-100 shadow-md">
            <div className='mx-5 w-3/12'>
                <ButtonGroup
                    buttons={[{
                        text: 'Confirm',
                        onClick: onConfirm
                    }, 
                    {
                        text: 'Run',
                        onClick: runCode,
                        overrideClass: `ml-5 w-32 px-4 py-1 rounded font-semibold border focus:outline-none text-black ${!code? 'text-gray-400 cursor-default' : 'text-black cursor-pointer'}`,
                        customStyle: { backgroundColor: !!code ? '#4bd699' : 'inherit' }
                    },
                    {
                        text: 'Undo',
                        onClick: onUndo
                    },
                    {
                        text: 'Revert',
                        onClick: onRevert
                    },
                    {
                        text: 'Download',
                        onClick: onDownload
                    }
                    ]}
                />
            </div>
        </div>

        <div className="w-full flex flex-nowrap">
            <div className='w-1/2 text-gray-500 font-semibold'>
                <div className='scroll w-full flex justify-center items-center' style={{height:'100%'}}>

                    <Label text="Results:">
                        <div id="display_results" style={{ whiteSpace: 'pre-wrap' }} >Select an operation to see preprocessed results</div>
                    </Label>
                    <Label text="">
                        <img id="img" src="" />
                    </Label>
                </div>
            </div>
            <div className='flex-grow-1 w-1/2' ref={codeParent}>
                {code ? code : <div className='w-full flex-grow-0 h-48 flex justify-center items-center text-gray-500 font-semibold'>
                    Select a filter to see the corresponding code
                </div>}
            </div>
        </div>

        <Table PageSize={10} />
    </div>)
}

export default CleaningR